/**
 * Issue #637 — "did the app itself just start tearing this page down?"
 *
 * WebKit reports a fetch killed by navigation or page teardown as
 * `TypeError: Load failed`, which is its wording for a real network outage
 * too. The two are byte-identical, so the ERROR cannot discriminate them and
 * the existing `AbortError` skip in `reportApiFailure` — which matches
 * Chromium's abort shape — never fires for them. The discriminator has to be
 * APP STATE, and this module is that state.
 *
 * Deliberately the smallest thing that answers the question: one timestamp, no
 * subscriptions, no React. It is imported by the reporter (`src/api/baseApi.js`)
 * on a hot path, so it must stay allocation-free and synchronous.
 */

/**
 * How recently a teardown must have been marked for an in-flight fetch failure
 * to be attributed to it.
 *
 * Two constraints squeeze this from both sides:
 *   - LOWER: the killed request dies within milliseconds of the navigation.
 *     COMPASSCARE-FRONTEND-1V measured 9 ms between the navigation breadcrumb
 *     and the dead poll. A window of seconds covers that by three orders of
 *     magnitude, with room for a slow device and a redirect that runs
 *     teardown work first.
 *   - UPPER: while the window is open, a GENUINE outage is downgraded to a
 *     breadcrumb. So it must be far shorter than the time in which a caregiver
 *     could plausibly hit a real network failure and expect it reported —
 *     seconds, never tens of seconds, and never open-ended.
 *
 * 2 s sits between those. It is not a tuning knob for noise volume: if events
 * still arrive, the fix is a missing `markTeardown()` call site, not a longer
 * window.
 */
export const TEARDOWN_WINDOW_MS = 2000;

/**
 * Module-level, and a plain reassigned number rather than a mutated object —
 * the house immutability rule is about not mutating shared objects in place,
 * and there is no object here to mutate.
 *
 * `null` means "no teardown has ever been marked", which is distinct from a
 * stale timestamp and keeps the first call cheap.
 */
let teardownAtMs = null;

/** Record that the app has begun tearing the page down.
 *
 *  Called by the three sites that perform a full-document navigation at
 *  session end — idle expiry, cross-tab session end, refresh-failure
 *  force-logout — and by the `pagehide` listener below. Deliberately NOT
 *  called by `AuthContext.logout()`: five of its six call sites never
 *  navigate, so marking there suppressed real outages after a manual
 *  sign-out. Idempotent: a later mark simply extends the window. */
export function markTeardown(nowMs = Date.now()) {
  teardownAtMs = nowMs;
}

/**
 * True when a teardown was marked within the last `TEARDOWN_WINDOW_MS`.
 *
 * The lower bound (`elapsed >= 0`) is not decoration: a system-clock step
 * backwards would otherwise make a long-past mark read as in flight, which
 * fails OPEN into silence — the one direction that hides a real outage.
 */
export function isTeardownInFlight(nowMs = Date.now()) {
  if (teardownAtMs === null) return false;
  const elapsed = nowMs - teardownAtMs;
  return elapsed >= 0 && elapsed <= TEARDOWN_WINDOW_MS;
}

/** Clear the mark. Called by the `pageshow` listener below when the page comes
 *  back from the back/forward cache — the one case where a teardown really is
 *  cancelled rather than completed. Otherwise a teardown is never cancelled and
 *  the window simply expires. Also lets a test assert from a known state. */
export function resetTeardownState() {
  teardownAtMs = null;
}

/**
 * Self-registering, so no application entry point has to wire it and this file
 * owns the whole discriminator. `pagehide` (not `unload`) is the event WebKit
 * actually fires on navigation and on entering the back/forward cache, and it
 * is the one Safari does not penalise.
 *
 * `pageshow` is the necessary other half, and its absence was a review finding.
 * `pagehide` fires on bfcache ENTRY as well as on a real unload, and a page
 * restored from the bfcache is live and active again — the caregiver is looking
 * at it. Without the clear, a mark taken on the way out would still be inside
 * its window on the way back in, and it would suppress a capture for a genuine
 * outage on a page that is not tearing down at all. That is the one way this
 * fix could lose a real event, so it fails closed: `persisted === true` is a
 * restore, and a restore cancels the teardown.
 *
 * Guarded twice: `typeof window` for the non-DOM test/SSR case, and a
 * module-level boolean that prevents double-registration WITHIN one module
 * instance.
 *
 * It does not prevent stacking across a hot module replacement, and an earlier
 * version of this comment claimed it did (review finding). A re-evaluation gets
 * a fresh `false` while the previously attached anonymous handlers stay on
 * `window`, so each hot update adds a pair. That is left alone deliberately
 * rather than plumbed with a dispose hook: both handlers are idempotent —
 * N `markTeardown()` calls write the same timestamp and N `resetTeardownState()`
 * calls clear the same one — so stacked copies change nothing observable, and
 * HMR does not exist in a production build.
 */
let listenersRegistered = false;

if (typeof window !== "undefined" && !listenersRegistered) {
  listenersRegistered = true;
  window.addEventListener("pagehide", () => markTeardown());
  // `=== true` deliberately, not truthiness: `pageshow` also fires on every
  // ordinary page load, and only the persisted flag says a bfcache restore
  // happened. A fresh load has no teardown to cancel.
  window.addEventListener("pageshow", (event) => {
    if (event && event.persisted === true) resetTeardownState();
  });
}
